(() => {
  const LEGACY_BONUS_PROPERTY = "_practitioner_bonus";
  const LEGACY_BONUS_PROPERTY_VALUE = "true";
  const PAID_QUANTITY_PER_BONUS = 12;
  const SYNC_DELAY = 75;
  const MAX_TIMER_DELAY = 2147483647;
  const PROMOTION_TITLE_PATTERN = /buy\s*12.*get\s*1\s*free/i;
  const LOG_PREFIX = "[Practitioner B12G1]";

  let syncTimer = null;
  let endBoundaryTimer = null;
  let syncInProgress = false;
  let syncRequested = false;

  function getCustomerTags() {
    const customerData = document.getElementById("customer-data");
    if (!customerData) return [];

    return String(customerData.dataset.customerTags || "")
      .split(",")
      .map(tag => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  function isEligibleCustomer() {
    return (
      getCustomerTags().includes("practitioner") &&
      getPromotionScheduleState().active
    );
  }

  function getPromotionScheduleState(now = Date.now()) {
    const settings = window.theme?.settings || {};
    if (settings.enablePractitionerBuy12Get1 !== true) {
      return { active: false, nextBoundary: null };
    }

    const endAt = String(settings.practitionerBuy12Get1EndAt || "").trim();
    if (!endAt) {
      console.log(
        `${LOG_PREFIX} No promotion end date is configured; the promotion remains active while enabled.`
      );
      return { active: true, nextBoundary: null };
    }

    const parsePacificDateTime = window.theme?.pacificTime?.parseDateTime;
    if (!parsePacificDateTime) {
      console.error(
        `${LOG_PREFIX} Could not evaluate the promotion end date because the Pacific date parser is unavailable.`,
        { configuredEndAtPacific: endAt }
      );
      return { active: false, nextBoundary: null };
    }

    const endTimestamp = parsePacificDateTime(endAt);

    // A configured but malformed date fails closed.
    if (!Number.isFinite(endTimestamp)) {
      console.error(
        `${LOG_PREFIX} Could not parse the promotion end date; the promotion is inactive. Use YYYY-MM-DD HH:MM in Pacific time.`,
        { configuredEndAtPacific: endAt }
      );
      return { active: false, nextBoundary: null };
    }
    if (now >= endTimestamp) {
      console.log(
        `${LOG_PREFIX} The promotion end date has passed; the promotion is inactive.`,
        {
          configuredEndAtPacific: endAt,
          currentTimestamp: new Date(now).toISOString(),
          endTimestamp: new Date(endTimestamp).toISOString(),
        }
      );
      return { active: false, nextBoundary: null };
    }

    console.log(
      `${LOG_PREFIX} The promotion end date is valid and has not passed; the promotion is active.`,
      {
        configuredEndAtPacific: endAt,
        currentTimestamp: new Date(now).toISOString(),
        endTimestamp: new Date(endTimestamp).toISOString(),
      }
    );
    return { active: true, nextBoundary: endTimestamp };
  }

  function isLegacyBonusLine(item) {
    return (
      String(item?.properties?.[LEGACY_BONUS_PROPERTY] || "").toLowerCase() ===
      LEGACY_BONUS_PROPERTY_VALUE
    );
  }

  function getPromotionFreeQuantity(item) {
    const quantity = Math.max(0, Number(item?.quantity) || 0);
    if (quantity === 0) return 0;

    const matchingAllocations = (
      item?.line_level_discount_allocations || []
    ).filter(allocation =>
      PROMOTION_TITLE_PATTERN.test(
        String(allocation?.discount_application?.title || "")
      )
    );
    if (matchingAllocations.length === 0) return 0;

    if (Number(item.final_price) === 0 || Number(item.final_line_price) === 0) {
      return quantity;
    }

    const originalUnitPrice = Number(item.original_price) || 0;
    if (originalUnitPrice <= 0) return 0;

    const promotionDiscountAmount = matchingAllocations.reduce(
      (total, allocation) => total + (Number(allocation.amount) || 0),
      0
    );
    return Math.min(
      quantity,
      Math.floor(promotionDiscountAmount / originalUnitPrice)
    );
  }

  function buildSyncPlan(cart, eligibleCustomer = isEligibleCustomer()) {
    const quantityByVariant = new Map();
    const updates = {};

    (cart?.items || []).forEach(item => {
      const variantId = String(item.variant_id || item.id || "");
      const quantity = Math.max(0, Number(item.quantity) || 0);
      if (!variantId || quantity === 0) return;

      if (isLegacyBonusLine(item)) {
        updates[item.key] = 0;
        return;
      }

      const promotionFreeQuantity = getPromotionFreeQuantity(item);
      const variantQuantity = quantityByVariant.get(variantId) || {
        paid: 0,
        free: 0,
      };
      variantQuantity.paid += Math.max(0, quantity - promotionFreeQuantity);
      variantQuantity.free += promotionFreeQuantity;
      quantityByVariant.set(variantId, variantQuantity);
    });

    const additions = [];

    if (eligibleCustomer) {
      quantityByVariant.forEach(({ paid, free }, variantId) => {
        const requiredFreeQuantity = Math.floor(paid / PAID_QUANTITY_PER_BONUS);
        const missingFreeQuantity = Math.max(0, requiredFreeQuantity - free);
        if (missingFreeQuantity > 0) {
          additions.push({
            id: Number(variantId),
            quantity: missingFreeQuantity,
          });
        }
      });
    }

    return { updates, additions };
  }

  function getCartRoute(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.replace(/\/?$/, "/")}${path}`;
  }

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        data?.description ||
          data?.message ||
          `Cart request failed (${response.status})`
      );
    }

    return data;
  }

  function hasRenderedLegacyBonusLine() {
    return Boolean(
      document.querySelector("[data-practitioner-buy-12-legacy-bonus]")
    );
  }

  async function syncCartOnce() {
    const eligibleCustomer = isEligibleCustomer();
    if (!eligibleCustomer && !hasRenderedLegacyBonusLine()) return false;

    const cart = await requestJson(getCartRoute("cart.js"), {
      cache: "no-store",
    });
    const { updates, additions } = buildSyncPlan(cart, eligibleCustomer);

    if (Object.keys(updates).length > 0) {
      await requestJson(getCartRoute("cart/update.js"), {
        method: "POST",
        body: JSON.stringify({ updates }),
      });
    }

    if (additions.length > 0) {
      await requestJson(getCartRoute("cart/add.js"), {
        method: "POST",
        body: JSON.stringify({ items: additions }),
      });
    }

    const cartChanged = Object.keys(updates).length > 0 || additions.length > 0;
    if (cartChanged) {
      document.dispatchEvent(
        new CustomEvent("theme:cart:refresh", { bubbles: true })
      );
    }

    return cartChanged;
  }

  async function runSync() {
    if (syncInProgress) {
      syncRequested = true;
      return;
    }

    syncInProgress = true;
    try {
      do {
        syncRequested = false;
        await syncCartOnce();
      } while (syncRequested);
    } catch (error) {
      console.error(`${LOG_PREFIX} Promotion sync error:`, error);
    } finally {
      syncInProgress = false;
    }
  }

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(runSync, SYNC_DELAY);
  }

  function scheduleEndBoundary() {
    window.clearTimeout(endBoundaryTimer);
    endBoundaryTimer = null;

    const scheduleState = getPromotionScheduleState();
    if (!scheduleState.active || scheduleState.nextBoundary === null) return;

    const delay = Math.max(0, scheduleState.nextBoundary - Date.now());
    endBoundaryTimer = window.setTimeout(
      () => {
        scheduleEndBoundary();
        scheduleSync();
      },
      Math.min(delay, MAX_TIMER_DELAY)
    );
  }

  [
    "theme:cart:load",
    "theme:cart:change",
    "theme:product:added",
    "theme:cart:refresh",
  ].forEach(eventName => document.addEventListener(eventName, scheduleSync));

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        scheduleEndBoundary();
        scheduleSync();
      },
      { once: true }
    );
  } else {
    scheduleEndBoundary();
    scheduleSync();
  }

  window.BiorayPractitionerBuy12 = {
    buildSyncPlan,
    getPromotionFreeQuantity,
    getPromotionScheduleState,
    isEligibleCustomer,
    scheduleSync,
  };
})();
